import {
	Dialog,
	Form,
	FormItem,
	FormattedText,
	Icon,
	TextInput,
	Toolbar,
	ValidationError,
	renderReactDialog
} from "client/Components";
import {
	AlertManager,
	useAlerts,
	useDataInputDialog,
	useStableImperativeCallback,
	useUnmountedCancellation,
	useValueEqualMemo
} from "client/Hooks";
import {
	ensureNotNil,
	generateTrackingId,
	isNil,
	setTrackingId
} from "client/Misc/utils";
import { IntegrationCredentialsEvents, publish } from "./events";
import {
	CreateIntegrationCredentialsSaveModel,
	IntegrationCredentialsListItem,
	LinkConfiguration,
	LinkToIntegrationCredentialsDialogViewModel,
	addLinksToCredentials,
	create,
	getLinkToIntegrationCredentialsDialogViewModel
} from "client/Api/IntegrationCredentials/integrationCredentials";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import {
	ReactElement,
	useCallback,
	useEffect,
	useState
} from "react";
import styles from "./LinkToIntegrationCredentialsDialog.module.less";
import classNames from "classnames";
import {
	IRootValidation,
	InputValidationResult,
	InputValidationResultKind,
	createValidationConfiguration
} from "client/Validation";
import { IFormHandler, useFormHandler } from "client/Forms";
import { inline, isNotEmpty } from "client/Validation/rules";

export type LinkToIntegrationCredentialsDialogProps = {
	thingToLink: ThingToLink;
	disableDialogCancellation?: boolean;
	onLinked: () => void;
	onClose: () => void;
};

export const LinkToIntegrationCredentialsDialog = (props: LinkToIntegrationCredentialsDialogProps): ReactElement => {
	const {
		thingToLink,
		disableDialogCancellation = false,
		onLinked,
		onClose
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [activeStep, setActiveStep] = useState<Step>("choose");

	const [viewModel, setViewModel] = useState<LinkToIntegrationCredentialsDialogViewModel>({
		linkName: "",
		credentials: []
	});

	const alertManager: AlertManager = useAlerts();

	const linkConfiguration = useValueEqualMemo(
		(): LinkConfiguration => {
			const linkConfiguration: LinkConfiguration = {
				exchange: false,
				fileServers: false,
				environmentApps: [],
				integrationFlowAuthentications: []
			};

			switch (thingToLink.kind) {
				case LinkKind.Exchange:
					linkConfiguration.exchange = true;
					break;
				case LinkKind.FileServers:
					linkConfiguration.fileServers = true;
					break;
				case LinkKind.EnvironmentApp:
					linkConfiguration.environmentApps = [
						...linkConfiguration.environmentApps,
						thingToLink.id
					];
					break;
				case LinkKind.IntegrationFlowAuthentication:
					linkConfiguration.integrationFlowAuthentications = [
						...linkConfiguration.integrationFlowAuthentications,
						thingToLink.id
					];
					break;
				default:
					never(thingToLink);
			}

			return linkConfiguration;
		},
		[thingToLink]
	);

	const stableOnClose: typeof onClose = useStableImperativeCallback(onClose);

	useEffect(
		(): (() => void) => {
			const load = async (signal: AbortSignal): Promise<void> => {
				setIsBusy(true);
				try {
					const viewModel: LinkToIntegrationCredentialsDialogViewModel = await getLinkToIntegrationCredentialsDialogViewModel(
						linkConfiguration,
						signal
					);

					setViewModel(viewModel);
					setIsBusy(false);

					const hasAnyExistingCredentials: boolean = viewModel.credentials
						.length > 0;

					setActiveStep(
						hasAnyExistingCredentials
							? "choose"
							: "create"
					);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_UnknownError"));
					stableOnClose();
				}
			};

			const cancellation = new AbortController();
			load(cancellation.signal);
			return () => cancellation.abort();
		},
		[
			alertManager,
			linkConfiguration,
			stableOnClose
		]
	);

	const onCancel: (() => void) | undefined = disableDialogCancellation
		? undefined
		: onClose;

	const linkedHandler = useCallback(
		(): void => {
			if (linkConfiguration.exchange) {
				publish(IntegrationCredentialsEvents.ExchangeLinked, undefined);
			}

			if (linkConfiguration.fileServers) {
				publish(IntegrationCredentialsEvents.FileServersLinked, undefined);
			}

			linkConfiguration.environmentApps
				.forEach((environmentAppId) => {
					publish(IntegrationCredentialsEvents.EnvironmentAppLinked, { id: environmentAppId });
				});

			linkConfiguration.integrationFlowAuthentications
				.forEach((authenticationId): void => {
					publish(IntegrationCredentialsEvents.IntegrationFlowAuthenticationLinked, { id: authenticationId });
				});

			onLinked();
		},
		[
			linkConfiguration.environmentApps,
			linkConfiguration.exchange,
			linkConfiguration.fileServers,
			linkConfiguration.integrationFlowAuthentications,
			onLinked
		]
	);

	return (
		<Dialog
			hasBusyIndicator={isBusy}
			trackingId="link-to-integration-credentials-dialog"
		>
			{activeStep === "choose"
				? (
					<ChooseStep
						name={viewModel.linkName}
						credentials={viewModel.credentials}
						configuration={linkConfiguration}
						onLinked={linkedHandler}
						onCancel={onCancel}
						onSwitchStep={() => setActiveStep("create")}
						onIsBusyChange={setIsBusy}
					/>
				)
				: (
					<CreateStep
						name={viewModel.linkName}
						configuration={linkConfiguration}
						onLinked={linkedHandler}
						onCancel={onCancel}
						onSwitchStep={() => setActiveStep("choose")}
						onIsBusyChange={setIsBusy}
					/>
				)
			}
		</Dialog>
	);
};
LinkToIntegrationCredentialsDialog.displayName = "LinkToIntegrationCredentialsDialog";

type ChooseStepProps = {
	name: string;
	credentials: readonly IntegrationCredentialsListItem[];
	configuration: LinkConfiguration;
	onLinked: () => void;
	onCancel: (() => void) | undefined;
	onSwitchStep: () => void;
	onIsBusyChange: (isBusy: boolean) => void;
};

const ChooseStep = (props: ChooseStepProps): ReactElement => {
	const {
		name,
		credentials,
		configuration,
		onLinked,
		onCancel,
		onSwitchStep,
		onIsBusyChange
	} = props;

	const [selectedCredentialId, setSelectedCredentialId] = useState<number | null>(null);

	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();

	const linkHandler = useCallback(
		async (): Promise<void> => {
			const credentialId: number = ensureNotNil(selectedCredentialId);

			onIsBusyChange(true);
			try {
				await addLinksToCredentials(
					credentialId,
					configuration,
					globalCancellation
				);

				onLinked();
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_ChangesNotSaved"));
				onIsBusyChange(false);
			}
		},
		[
			alertManager,
			configuration,
			globalCancellation,
			onIsBusyChange,
			onLinked,
			selectedCredentialId
		]
	);

	return (
		<>
			<Dialog.Header
				title={(
					<FormattedText
						text={localize("_SetCredentialsForLinkName")}
						thingsToReplace={[
							{ what: "{LinkName}", filler: `'${name}'` }
						]}
					/>
				)}
				onClose={onCancel}
			/>

			<Dialog.Body>
				<p>
					<FormattedText
						text={localize("_YouDontHaveCredentialsSetForLinkNameChooseCredentialsListedBelowOrUseOtherCredentials")}
						thingsToReplace={[
							{ what: "{LinkName}", filler: `'${name}'` }
						]}
					/>
				</p>

				<p className={styles.chooseCredentials}>
					{localize("_ChooseCredentials")}
				</p>

				<ul {...setTrackingId("choose-list")}>
					{credentials.map((x): ReactElement => (
						<LinkListItem
							key={x.id}
							icon="user"
							text={x.username}
							onClick={() => setSelectedCredentialId(x.id)}
							isSelected={x.id === selectedCredentialId}
							trackingId={generateTrackingId("existing", `|${x.id}`)}
						/>
					))}

					<LinkListItem
						icon="plus"
						text={localize("_UseOtherCredentials")}
						onClick={onSwitchStep}
						trackingId="create-new"
					/>
				</ul>
			</Dialog.Body>

			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize("_UseCredentials")}
						trackingId="use-credentials"
						isDisabled={isNil(selectedCredentialId)}
						onClick={linkHandler}
					/>

					{onCancel && (
						<Toolbar.Button
							kind="secondary"
							text={localize("_Cancel")}
							trackingId="cancel"
							onClick={onCancel}
						/>
					)}
				</Toolbar>
			</Dialog.Footer>
		</>
	);
};
ChooseStep.displayName = "ChooseStep";

type LinkListItemProps = {
	icon: "user" | "plus";
	text: string;
	isSelected?: boolean;
	onClick: () => void;
	trackingId: string;
};

const LinkListItem = (props: LinkListItemProps): ReactElement => {
	const {
		icon,
		text,
		isSelected = false,
		onClick,
		trackingId
	} = props;

	return (
		<li
			{...setTrackingId(trackingId)}
			className={classNames(
				styles.listItem,
				{ [styles.listItem_selected]: isSelected }
			)}
			onClick={onClick}
		>
			<div className={styles.listItemImage}>
				<Icon
					value={icon}
					size="extra-extra-large"
				/>
			</div>

			<span className={styles.listItemText}>
				{text}
			</span>

			<div className={styles.listItemCheckmark}>
				<Icon
					value="check"
					size="extra-large"
				/>
			</div>
		</li>
	);
};
LinkListItem.displayName = "LinkListItem";

type CreateStepProps = {
	name: string;
	configuration: LinkConfiguration;
	onLinked: () => void;
	onCancel: (() => void) | undefined;
	onSwitchStep: () => void;
	onIsBusyChange: (isBusy: boolean) => void;
};

const CreateStep = (props: CreateStepProps): ReactElement => {
	const {
		name,
		configuration,
		onLinked,
		onCancel,
		onSwitchStep,
		onIsBusyChange
	} = props;

	const [formData, setFormData] = useState<FormData>({
		username: "",
		password: "",
		confirmPassword: ""
	});

	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();

	const formHandler: IFormHandler<FormData> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const createHandler = useCallback(
		async (): Promise<void> => {
			onIsBusyChange(true);

			const validation: IRootValidation<FormData> = formHandler.validation;
			try {
				const validationResult: InputValidationResult = await validation
					.validate(globalCancellation);

				if (validationResult.kind !== InputValidationResultKind.Valid) {
					onIsBusyChange(false);
					return;
				}

				const model: CreateIntegrationCredentialsSaveModel = {
					...formData,
					links: configuration
				};

				await create(model, globalCancellation);
				onLinked();
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				if (!validation.tryProcessServerError(e)) {
					console.error(e);
					alertManager.showError(localize("_ChangesNotSaved"));
				}

				onIsBusyChange(false);
			}
		},
		[
			alertManager,
			configuration,
			formData,
			formHandler.validation,
			globalCancellation,
			onIsBusyChange,
			onLinked
		]
	);

	return (
		<>
			<Dialog.Header
				title={(
					<FormattedText
						text={localize("_SetCredentialsForLinkName")}
						thingsToReplace={[
							{ what: "{LinkName}", filler: `'${name}'` }
						]}
					/>
				)}
				onClose={onCancel}
			/>

			<Dialog.Body>
				<p>
					<FormattedText
						text={localize("_YouDontHaveCredentialsSetForLinkNameEnterYourUsernameAndPasswordBelow")}
						thingsToReplace={[
							{ what: "{LinkName}", filler: `'${name}'` }
						]}
					/>
				</p>

				<Form onSubmit={createHandler}>
					<FormItem
						label={localize("_Username")}
						isRequired
					>
						<TextInput
							{...formHandler.for("username").attachToTextInput()}
							autoFocus="focus"
						/>

						<ValidationError {...formHandler.for("username").attachToValidationError()} />
					</FormItem>

					<FormItem
						label={localize("_Password")}
						isRequired
					>
						<TextInput
							{...formHandler.for("password").attachToTextInput()}
							type="password"
						/>

						<ValidationError {...formHandler.for("password").attachToValidationError()} />
					</FormItem>

					<FormItem
						label={localize("_ConfirmPassword")}
						isRequired
					>
						<TextInput
							{...formHandler.for("confirmPassword").attachToTextInput()}
							type="password"
						/>

						<ValidationError {...formHandler.for("confirmPassword").attachToValidationError()} />
					</FormItem>
				</Form>
			</Dialog.Body>

			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="secondary"
						text={localize("_Back")}
						trackingId="back"
						onClick={onSwitchStep}
					/>

					<Toolbar.Button
						kind="primary"
						text={localize("_SetCredentials")}
						trackingId="set-credentials"
						onClick={createHandler}
					/>

					{onCancel && (
						<Toolbar.Button
							kind="secondary"
							text={localize("_Cancel")}
							trackingId="cancel"
							onClick={onCancel}
						/>
					)}
				</Toolbar>
			</Dialog.Footer>
		</>
	);
};
CreateStep.displayName = "CreateStep";

type FormData = Omit<CreateIntegrationCredentialsSaveModel, "links">;

const validationConfiguration = createValidationConfiguration<FormData>();

validationConfiguration.forScalar("username")
	.add(
		isNotEmpty()
	);

validationConfiguration.forScalar("password")
	.add(
		isNotEmpty()
	);

validationConfiguration.forScalar("confirmPassword")
	.add(
		isNotEmpty(),
		inline(
			(value, context): InputValidationResult => {
				const parent = context.parentValue as FormData;
				const password: string = parent.password;

				if (!password
					|| !value
					|| value === password
				) {
					return {
						kind: InputValidationResultKind.Valid
					};
				}

				return {
					kind: InputValidationResultKind.Invalid,
					errorMessage: localize("_ValidationPasswordConfirm")
				};
			}
		)
	);

type Step = "choose" | "create";

export enum LinkKind {
	Exchange = 1,
	FileServers = 2,
	EnvironmentApp = 3,
	IntegrationFlowAuthentication = 4
}

export type ExchangeLink = {
	kind: LinkKind.Exchange;
};

export type FileServersLink = {
	kind: LinkKind.FileServers;
};

export type EnvironmentAppLink = {
	kind: LinkKind.EnvironmentApp;
	id: number;
};

export type IntegrationFlowAuthenticationLink = {
	kind: LinkKind.IntegrationFlowAuthentication;
	id: number;
};

export type ThingToLink =
	| ExchangeLink
	| FileServersLink
	| EnvironmentAppLink
	| IntegrationFlowAuthenticationLink;

type LinkToIntegrationCredentialsDialogInitialData = Omit<LinkToIntegrationCredentialsDialogProps, "onClose" | "onLinked">;

export function useLinkToIntegrationCredentialsDialog(): [
	element: ReactElement | null,
	open: (props: LinkToIntegrationCredentialsDialogInitialData) => Promise<TryResult<void>>
] {
	return useDataInputDialog(
		(props, onClose, onLinked) => (
			<LinkToIntegrationCredentialsDialog
				{...props}
				onClose={onClose}
				onLinked={onLinked}
			/>
		)
	);
}

export function openLinkToIntegrationCredentialsDialog(
	props: LinkToIntegrationCredentialsDialogInitialData
): Promise<TryResult<void>> {
	return new Promise<TryResult<void>>(
		(resolve): void => {
			renderReactDialog(
				(onClose) => (
					<LinkToIntegrationCredentialsDialog
						{...props}
						onClose={(): void => {
							onClose();

							resolve({
								wasSuccessful: false
							});
						}}
						onLinked={(): void => {
							onClose();

							resolve({
								wasSuccessful: true,
								result: undefined
							});
						}}
					/>
				)
			);
		}
	);
}
